import { useMemo } from "react";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { useSealinesQuery } from "entities/Sealines";
import {
  SeaSavedCardModel,
  seaSavedCardStore,
  UpdateCardData,
  useUpdateSingleCardMutation,
} from "entities/SeaSavedCard";
import {
  createSeaSearchQueryParams,
  SeaSearchCardModel,
  useBulkUpdateSearchQuery,
  useSearchQuery,
} from "entities/SeaSearchCard";
import { resolveDateValues } from "entities/shared/utils";
import { subscriptionStore } from "entities/Subscription";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { Nullable } from "shared/lib/typeCheck.lib";
import { formatFollowCardDate, getUtcTimestamp } from "shared/utils/dateTime.utils";

type ShouldUpdateCardFunc = () => boolean;
type UpdateCardFunc = (model: SeaSavedCardModel) => void;
type UpdateAllCardsFunc = (cards: SeaSavedCardModel[]) => Promise<{
  results: { reference: string | null; isSuccess: boolean }[];
  canUpdateMore: boolean;
}>;
type UsePrepareCardUpdateActionsReturnType = {
  updateCard: UpdateCardFunc;
  updateAllCards: UpdateAllCardsFunc;
  shouldUpdateCard: ShouldUpdateCardFunc;
  isResolvingUpdatePossibility: boolean;
  isCardUpdateSuccess: boolean;
  isCardUpdating: boolean;
};

type UseSeaCardUpdateActionsParams = {
  model?: Nullable<SeaSavedCardModel>;
  isOpenedCardExist?: boolean;
  openedCardUpdateAction?: (response: SeaSavedCardModel) => {
    type: string;
    payload: SeaSavedCardModel | SeaSearchCardModel | null;
  };
  updateFilteredCards?: (response: SeaSavedCardModel) => {
    type: string;
    payload: SeaSavedCardModel;
  };
  updateCardByTagAction?: (response: SeaSavedCardModel) => {
    type: string;
    payload: SeaSavedCardModel;
  };
};

export const createSeaUpdateCardHook = ({
  model,
  isOpenedCardExist,
  openedCardUpdateAction,
  updateFilteredCards,
  updateCardByTagAction,
}: UseSeaCardUpdateActionsParams = {}): (() => UsePrepareCardUpdateActionsReturnType) => {
  return function useSavedCardUpdateActions() {
    const dispatch = useAppDispatch();
    const { sealines } = useSealinesQuery();
    const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);

    // hook for single update
    const { searchRequest: singleSearchRequest, isSearchLoading: isSingleSearchLoading } = useSearchQuery({
      queryKey: [`card-update-request-${model?.reference}`],
      isAuthenticated,
      setSubscription: (subscription) => ({
        type: subscriptionStore.actions.setSubscription.type,
        payload: subscription,
      }),
      setActivePaymentModal: (activePaymentModal) => ({
        type: subscriptionStore.actions.setActivePaymentModal.type,
        payload: activePaymentModal,
      }),
      setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
        type: appStore.actions.setAppIntegrationRequestLimitReached.type,
        payload: appIntegrationRequestLimitReached,
      }),
      setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
        type: appStore.actions.setWebAppRequestLimitReached.type,
        payload: webAppRequestLimitReached,
      }),
    });

    // hook for bulk update
    const { searchRequest: bulkSearchRequest, isSearchLoading: isBulkSearchLoading } = useBulkUpdateSearchQuery({
      isAuthenticated,
      setSubscription: (subscription) => ({
        type: subscriptionStore.actions.setSubscription.type,
        payload: subscription,
      }),
      setActivePaymentModal: (activePaymentModal) => ({
        type: subscriptionStore.actions.setActivePaymentModal.type,
        payload: activePaymentModal,
      }),
      setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
        type: appStore.actions.setAppIntegrationRequestLimitReached.type,
        payload: appIntegrationRequestLimitReached,
      }),
      setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
        type: appStore.actions.setWebAppRequestLimitReached.type,
        payload: webAppRequestLimitReached,
      }),
    });

    const { updateCard: updateCardRequest, isCardUpdateSuccess, isCardUpdateLoading } = useUpdateSingleCardMutation();

    const isResolvingUpdatePossibility = useMemo(
      () => isSingleSearchLoading || isBulkSearchLoading || isCardUpdateLoading,
      [isSingleSearchLoading, isBulkSearchLoading, isCardUpdateLoading]
    );

    const prepareUpdateData = (searchCardModel: SeaSearchCardModel): UpdateCardData => {
      const preparedData: UpdateCardData = {};

      if (!model || !searchCardModel) {
        return preparedData;
      }

      const tags = model.tags;
      if (tags.length) {
        preparedData.tags = tags;
      }

      const currentPin = searchCardModel.currentPin;
      if (currentPin) {
        preparedData.location_pin = currentPin;
      }

      if (searchCardModel.reference) {
        preparedData.reference = searchCardModel.reference;
      }

      if (searchCardModel.fromDirection) {
        preparedData.location_from = searchCardModel.fromDirection;
      }

      if (searchCardModel.toDirection) {
        preparedData.location_to = searchCardModel.toDirection;
      }

      if (searchCardModel.referenceType) {
        preparedData.type = searchCardModel.referenceType;
      }

      const carrierScac = searchCardModel?.getCarrierScac();
      if (carrierScac) {
        preparedData.carrier_scac = carrierScac;
      }

      if (searchCardModel.carrierName) {
        preparedData.carrier_name = searchCardModel.carrierName;
      }

      if (searchCardModel.status) {
        preparedData.status = searchCardModel.status;
      }
      preparedData.equipments_count = searchCardModel.containersAmount;

      const { atd, etd, ata, eta, predictiveETA } = searchCardModel;
      const { departureDate, arrivalDate } = resolveDateValues({ atd, etd, ata, eta, predictiveETA });
      const departureDateWithLocale = departureDate.value ? formatFollowCardDate(departureDate.value) : null;
      const arrivalDateWithLocale = arrivalDate.value ? formatFollowCardDate(arrivalDate.value) : null;

      if (departureDateWithLocale) {
        preparedData.date_from = departureDateWithLocale;
      }
      preparedData.date_from_actual = departureDate.isActual;

      if (arrivalDateWithLocale) {
        preparedData.date_to = arrivalDateWithLocale;
      }
      preparedData.date_to_actual = arrivalDate.isActual;

      preparedData.date_to_predictive = arrivalDate.isPredictive ? arrivalDate.value : null;

      const sizeTypes = searchCardModel.containersSizeTypes;
      if (sizeTypes.length) {
        preparedData.size_type = JSON.stringify(sizeTypes);
      }

      return preparedData;
    };

    const shouldUpdateCard: ShouldUpdateCardFunc = () => {
      if (!isAuthenticated || !model || !sealines || !sealines?.length) return false;

      const dateValueToCompare = model.updatedAt;
      if (!dateValueToCompare) return false;

      const dateToCompare = getUtcTimestamp(dateValueToCompare);

      const carrierScac = model.carrierScac;
      if (!carrierScac) return false;

      const foundCarrierItem = sealines.find(
        (d) => d.code === carrierScac || d.identifiers?.scac_codes?.includes(carrierScac)
      );
      if (!foundCarrierItem) return false;

      const cacheInterval = 0;
      const cacheIntervalMilliseconds = cacheInterval * 1000;
      const currentDate = getUtcTimestamp(new Date());
      return dateToCompare + cacheIntervalMilliseconds < currentDate;
    };

    const updateCard: UpdateCardFunc = (model) => {
      const reqParams = createSeaSearchQueryParams({
        model: {
          reference: model.reference,
          referenceType: model.referenceType,
          carrierScac: model.carrierScac,
        },
        requestType: "updateCard",
      });

      if (!model || !reqParams || isResolvingUpdatePossibility) return;
      singleSearchRequest(reqParams, {
        onSuccess: (searchResponse) => {
          if (searchResponse?.requestLimitReachedWebApp) return;
          updateCardRequest(
            { id: model.id, data: prepareUpdateData(searchResponse) },
            {
              onSuccess: (response) => {
                dispatch(seaSavedCardStore.actions.updateSavedCardAction(response));
                updateFilteredCards && dispatch(updateFilteredCards(response));
                updateCardByTagAction && dispatch(updateCardByTagAction(response));
                if (isOpenedCardExist && openedCardUpdateAction) {
                  dispatch(openedCardUpdateAction(response));
                }
              },
            }
          );
        },
      });
    };

    const updateAllCards: UpdateAllCardsFunc = async (cards) => {
      const results: { reference: string | null; isSuccess: boolean }[] = [];

      for (const card of cards) {
        const reqParams = createSeaSearchQueryParams({
          model: {
            reference: card.reference,
            referenceType: card.referenceType,
            carrierScac: card.carrierScac,
          },
          requestType: "updateCard",
        });

        if (!reqParams || isResolvingUpdatePossibility) {
          results.push({ reference: card.reference, isSuccess: false });
          continue;
        }

        try {
          const searchResponse = await new Promise<SeaSearchCardModel | null>((resolve, reject) => {
            bulkSearchRequest(reqParams, {
              onSuccess: (response) => resolve(response),
              onError: () => reject(null),
            });
          });

          if (!searchResponse) {
            results.push({ reference: card.reference, isSuccess: false });
            continue;
          }

          if (searchResponse?.requestLimitReachedWebApp) {
            results.push({ reference: card.reference, isSuccess: false });
            return { results, canUpdateMore: false };
          }

          const response = await new Promise<SeaSavedCardModel>((resolve) => {
            updateCardRequest(
              { id: card.id, data: prepareUpdateData(searchResponse) },
              {
                onSuccess: (result) => resolve(result),
              }
            );
          });
          results.push({ reference: card.reference, isSuccess: true });
          dispatch(seaSavedCardStore.actions.updateSavedCardAction(response));
          updateFilteredCards && dispatch(updateFilteredCards(response));
          updateCardByTagAction && dispatch(updateCardByTagAction(response));
        } catch (_) {
          results.push({ reference: card.reference, isSuccess: false });
          continue;
        }
      }
      return { results, canUpdateMore: true };
    };

    return {
      updateCard,
      updateAllCards,
      shouldUpdateCard,
      isResolvingUpdatePossibility,
      isCardUpdateSuccess,
      isCardUpdating: isCardUpdateLoading,
    };
  };
};
